import { useQueryClient } from '@tanstack/react-query';
import { Outlet, useLocation, useNavigate, useRouter } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SegmentedTabs } from '~/components/common/segmented-tabs';
import { Button } from '~/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import { Skeleton } from '~/components/ui/skeleton';

import { useRun } from '../hooks/use-run';
import {
  PRE_SCORE_PAGE_SIZE,
  runPreScoresQuery,
  runScorecardQuery,
  useRunScorecard,
} from '../hooks/use-run-content';
import { useRunLineageSummary } from '../hooks/use-run-lineage';
import { useRunProgress } from '../hooks/use-run-progress';
import { useRunTitle } from '../hooks/use-run-title';
import { catalogLabel } from '../utils/catalog-labels';
import { hasCoverage } from '../utils/content-results';
import { NavOriginCtx, useNavContext } from '../utils/nav-context';
import { RunBreadcrumbTailCtx, type RunBreadcrumbTail } from '../utils/run-breadcrumb-tail';
import { isRunLive } from '../utils/run-status';
import { activeRunTab, reachedContent, RUN_TABS, visibleTabs } from '../utils/run-tabs';
import { StageDestinationCtx } from '../utils/stage-destination-context';

import { CostGatePanel } from './cost-gate-panel';
import type { Crumb } from './page-breadcrumbs';
import { BackToOriginChip } from './page-breadcrumbs';
import { RunHeader } from './run-header';
import { RunPhaseStrip } from './run-phase-strip';

interface Props {
  runId: string;
}

/**
 * Frame for every `/catalog-v2/runs/<runId>/*` page: ONE header band (R1) — breadcrumb
 * (Runs › run › tab), name + state chip + the one primary action, a meta line — with the
 * run's progress as a one-line phase strip under it (R2), then the contextual tab strip — an open
 * gate badges Products (Option 2: its decision panel lives there), Recovery only once the run has
 * a failure point. The old Publishing tab is folded into Summary (2026-10-16). Below 768px the
 * strip becomes a `Select`.
 */
export function RunShell({ runId }: Props) {
  const { t } = useTranslation('catalog-v2-run');
  const { t: tCatalog } = useTranslation('catalog-v2');
  const { pathname, href } = useLocation();
  const navigate = useNavigate();
  const router = useRouter();
  const runQuery = useRun(runId);
  const progressQuery = useRunProgress(runId);
  const progress = progressQuery.data;
  const pendingGate = progress?.pendingGate ?? null;
  const queryClient = useQueryClient();
  const preScoreGateOpen = pendingGate?.stage === 'PRE_ENRICH_SCORE';
  // A score gate is waiting: warm the decision panel's two slow reads (≈2 s products + ≈1 s
  // scores) while the user is still on another tab, so opening Products shows the table at once.
  useEffect(() => {
    if (!preScoreGateOpen) return;
    void queryClient.prefetchQuery(runPreScoresQuery(runId, 1, PRE_SCORE_PAGE_SIZE));
    void queryClient.prefetchQuery(runScorecardQuery(runId));
  }, [preScoreGateOpen, queryClient, runId]);
  // Cheap existence check only (same cache entry the Issues tab itself reads): the Issues tab
  // shows once this batch has any failed or still-flagged row to group by reason.
  const lineageSummary = useRunLineageSummary(runId);
  const hasIssues =
    (lineageSummary.data?.stages.reduce((sum, s) => sum + s.failed + s.revisit, 0) ?? 0) > 0;
  // Insights is always in the strip but enabled only once coverage exists — the SAME scorecard
  // read (and `hasCoverage` rule) the Insights page draws, fetched only after content generation
  // is part of the batch so earlier stages never pay for it.
  const contentReached = reachedContent(progress);
  const scorecardQuery = useRunScorecard(runId, contentReached);
  const insightsReady = hasCoverage(scorecardQuery.data);
  const insightsTooltip = t(
    contentReached && isRunLive(progress?.runStatus)
      ? 'catalog-v2-run:runTabs.insightsDisabledRunning'
      : 'catalog-v2-run:runTabs.insightsDisabled',
  );
  const gateBadge = t('catalog-v2-run:tabs.gateWaiting');
  const visible = visibleTabs(progress, hasIssues);
  // R1-1 — this DESTINATION's own `from`/`back` (see `showOrigin` below).
  const nav = useNavContext();
  const active = activeRunTab(pathname, `/catalog-v2/runs/${runId}`);
  // A page whose owning tab isn't earned yet keeps that tab
  // in the strip while you're on it, so the highlighted tab and the breadcrumb always agree.
  const tabs = visible.some((tab) => tab.value === active)
    ? visible
    : RUN_TABS.filter((tab) => tab.value === active || visible.includes(tab));
  const activeTab = RUN_TABS.find((tab) => tab.value === active) ?? RUN_TABS[0];
  const { title, experimentId, experimentName } = useRunTitle(runQuery.data, progress);
  // Same resolved-connector value RunHeader shows ("Publish to X") — provided to the whole batch
  // subtree via context so every `stageLabel()` call under it picks it up without prop-drilling.
  const connector = progress?.connectorCode ?? runQuery.data?.connectorCode ?? null;
  const destination = connector ? catalogLabel(tCatalog, 'connector', connector) : null;
  // S8 — every cross-link rendered anywhere under this batch (a product cell, a flag badge, a
  // commerce-cell link…) picks up `from=batch:<runId>:<tab>` ambiently from here, never threaded
  // through as a prop; `backHref` is THIS page's own address, so the destination's "← Back to
  // <batch>" chip restores whatever tab/filters were open, not just the bare batch route.
  const navOrigin = useMemo(
    () => ({ origin: { kind: 'batch' as const, runId, tab: active }, backHref: href }),
    [runId, active, href],
  );

  /** Whether a tab carries the "Waiting" badge: Products for any open gate, Summary too for the
   *  APPROVAL gate (its decision band replaced the old Publishing tab). */
  const gateBadgesTab = (value: string): boolean =>
    !!pendingGate &&
    (value === 'products' || (value === 'overview' && pendingGate.stage === 'APPROVAL'));

  /** Navigates to the tab's route; shared by the desktop tab strip and the mobile select. */
  const goToTab = (value: string) => {
    const tab = RUN_TABS.find((r) => r.value === value);
    if (tab) void navigate({ to: tab.to, params: { runId } });
  };

  // R1-1 — this DESTINATION's own `from`/`back` (set by whatever cross-section link landed here,
  // e.g. a product's own history opening a specific batch attempt). Suppressed when the origin is
  // just this same batch (an ambient same-batch cross-link into a nested page would otherwise show
  // a prefix — and a back chip — that only repeats the batch the reader is already in).
  const showOrigin = !(nav.origin?.kind === 'batch' && nav.origin.runId === runId);
  // Design B — a same-batch link that carried its own `back` (a Products row's Compare › /
  // Journey ›) gets "← Back to Products", restoring that exact filtered list on return.
  const originTab = nav.origin?.kind === 'batch' ? nav.origin.tab : null;
  const sameBatchBack =
    !showOrigin && originTab && nav.back?.href && nav.back.href !== href
      ? {
          href: nav.back.href,
          labelKey: (RUN_TABS.find((tab) => tab.value === originTab) ?? RUN_TABS[0]).labelKey,
          // A link that named its exact spot (an Insights row's detail view) reads "← Back to <it>".
          label: nav.backLabel,
        }
      : null;
  const [tail, setTail] = useState<RunBreadcrumbTail | undefined>(undefined);
  const hasTail = !!tail && tail.crumbs.length > 0;
  const breadcrumbs: Crumb[] = [
    ...(showOrigin ? nav.originCrumbs : []),
    { label: tCatalog('breadcrumbs.runs'), to: '/catalog-v2/runs' },
    active === 'overview' && !hasTail
      ? { label: title }
      : { label: title, to: '/catalog-v2/runs/$runId', params: { runId } },
    ...(hasTail
      ? tail.crumbs
      : active === 'overview'
        ? []
        : [{ label: t(`catalog-v2-run:${activeTab.labelKey}`) }]),
  ];

  return (
    <RunBreadcrumbTailCtx.Provider value={setTail}>
      <NavOriginCtx.Provider value={navOrigin}>
        <StageDestinationCtx.Provider value={destination}>
          <div className="flex flex-col gap-3">
            <RunHeader
              runId={runId}
              run={runQuery.data}
              progress={progress}
              isLoading={runQuery.isLoading || progressQuery.isLoading}
              title={title}
              experimentId={experimentId}
              experimentName={experimentName}
              breadcrumbs={breadcrumbs}
              activeTab={active}
            >
              {progress ? (
                // R2: progress in one compact row. Its "Now · Next" sentence is the strip's tooltip
                // and sits in the header status chip's popover (click) — no extra line here.
                <RunPhaseStrip runId={runId} progress={progress} showNow={false} className="pt-1" />
              ) : progressQuery.isLoading ? (
                <Skeleton className="h-8 w-full" />
              ) : null}
            </RunHeader>
            {/* R1-1 — the ONE "← Back to <origin>" chip for the whole batch subtree; a nested
            page no longer renders its own (same `showOrigin` rule as the breadcrumb prefix
            above). */}
            {showOrigin && nav.back ? <BackToOriginChip /> : null}
            {sameBatchBack ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground h-auto w-fit cursor-pointer gap-1.5 rounded-full px-3 py-1 text-xs font-medium"
                onClick={() => {
                  router.history.push(sameBatchBack.href);
                }}
              >
                <ArrowLeft aria-hidden="true" className="size-3.5 shrink-0" />
                <span className="truncate">
                  {tCatalog('navContext.backToTab', {
                    tab: sameBatchBack.label ?? t(`catalog-v2-run:${sameBatchBack.labelKey}`),
                  })}
                </span>
              </Button>
            ) : null}
            {runQuery.isError && progressQuery.isError ? (
              <p role="alert" className="text-error text-sm">
                {t('catalog-v2-run:header.loadError')}
              </p>
            ) : null}

            {/* Tabs scroll with the page: pinned tabs sat on top of the tables' pinned headers. */}
            <div className="bg-card -mx-4 -mt-2 px-4 pt-2 pb-2 md:pb-0">
              {/* ≥768: the tab strip. <768: a Select, so the strip never needs horizontal scroll. */}
              <div className="hidden md:block">
                <SegmentedTabs
                  variant="underline"
                  value={active}
                  onChange={goToTab}
                  options={tabs.map((tab) => {
                    // Any open gate badges Products (its decision panel); the APPROVAL gate also
                    // badges Summary, whose top band now carries the publish decision.
                    const waiting = gateBadgesTab(tab.value);
                    const locked = tab.value === 'insights' && !insightsReady;
                    return {
                      value: tab.value,
                      label: t(`catalog-v2-run:${tab.labelKey}`),
                      ...(waiting ? { badge: gateBadge } : {}),
                      ...(locked ? { disabled: true, tooltip: insightsTooltip } : {}),
                    };
                  })}
                  ariaLabel={t('catalog-v2-run:runTabs.ariaLabel')}
                />
              </div>
              <div className="md:hidden">
                <Select value={active} onValueChange={goToTab}>
                  <SelectTrigger
                    className="w-full cursor-pointer"
                    aria-label={t('catalog-v2-run:runTabs.ariaLabel')}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {tabs.map((tab) => (
                      <SelectItem
                        key={tab.value}
                        value={tab.value}
                        disabled={tab.value === 'insights' && !insightsReady}
                        className="cursor-pointer data-[disabled]:cursor-not-allowed"
                      >
                        {t(`catalog-v2-run:${tab.labelKey}`)}
                        {gateBadgesTab(tab.value) ? ` · ${gateBadge}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* §95 — a cost gate holding the run shows on every tab, like an open gate's state. */}
            {progress?.costGate ? (
              <CostGatePanel runId={runId} waiting={progress.costGate} />
            ) : null}

            <Outlet />
          </div>
        </StageDestinationCtx.Provider>
      </NavOriginCtx.Provider>
    </RunBreadcrumbTailCtx.Provider>
  );
}
